import { DateTime } from 'luxon';
import { coerce, date, define, nullable, string } from 'superstruct';

/**
 * Ensure that a value is a valid ISO 8601 date and time string.
 *
 * Use `isoDateTimeWithCoercions` instead of using this directly.
 */
const isoDateTime = () => define('IsoDateTime', value => {
  /** For now, let's be extra restrictive with the format that we accept. */
  // eslint-disable-next-line require-unicode-regexp
  const pedanticIso8601 = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(.\d{1,3})?Z$/;
  if (typeof value === 'string' && pedanticIso8601.test(value) && DateTime.fromISO(value).isValid) {
    return true;
  } else {
    return {
      message: 'validations:Invalid ISO Date Time'
    };
  }
});

/**
 * Augment a struct to normalize ISO 8601 strings. This includes changing timezone to UTC and filling in missing precision.
 *
 * Note: You must use `mask(value, Struct)` on the value to have the coercion
 *       take effect! Using simply `assert()` or `is()` will not use coercion.
 */
export function normalizeIsoDateTime(struct) {
  return coerce(struct, string(), x => {
    const dt = DateTime.fromISO(x);
    return dt.isValid ? dt.toUTC().toISO() : x;
  });
}

/**
 * Augment a struct to replace instances of JavaScript's `Date` with their ISO 8601 string representations.
 *
 * Note: You must use `mask(value, Struct)` on the value to have the coercion
 *       take effect! Using simply `assert()` or `is()` will not use coercion.
 */
export function jsDateToIsoDateTime(struct) {
  return coerce(struct, date(), x => x.toISOString());
}

/**
 * Ensure that a value is a valid Luxon `DateTime`.
 *
 * Note: this also ensures that the value is not an invalid `DateTime`, which can occur when parsing a date fails but still returns a `DateTime`.
 */
export const luxonDateTime = () => define('LuxonDateTime', value => {
  if (value instanceof DateTime && value.isValid) {
    return true;
  } else {
    return {
      message: 'validations:Invalid Luxon Date Time'
    };
  }
});

/**
 * Augment a struct to replace instances of Luxon's `DateTime` with their ISO 8601 string representations.
 *
 * Note: You must use `mask(value, Struct)` on the value to have the coercion
 *       take effect! Using simply `assert()` or `is()` will not use coercion.
 */
export function luxonToIsoDateTime(struct) {
  return coerce(struct, luxonDateTime(), x => x.toUTC().toISO());
}

/**
 * Ensure that a value is a valid ISO 8601 date and time string.
 *
 * Also coerce JS `Date` and Luxon `DateTime` to ISO format and normalize.
 */
export const isoDateTimeWithCoercions = () => jsDateToIsoDateTime(luxonToIsoDateTime(isoDateTime()));
const nullableLuxonDateTime = () => define('LuxonDateTime', value => {
  if (value instanceof DateTime) {
    return true;
  } else {
    return {
      message: 'validations:Invalid Luxon Date Time'
    };
  }
});
function nullableJsDateToIsoDateTime(struct) {
  return coerce(struct, date(), x => Number.isNaN(x.getSeconds()) ? null : x.toISOString());
}
function nullableLuxonToIsoDateTime(struct) {
  return coerce(struct, nullableLuxonDateTime(), x => x.isValid ? x.toUTC().toISO() : null);
}

/**
 * Ensure that a value is a valid ISO 8601 date and time string.
 * Similar to {@link isoDateTimeWithCoercions}, but coerces invalid
 * JS `Date` and Luxon `DateTime` to `null` instead of throwing an error.
 */
export const nullableIsoDateTimeWithCoercions = () => nullableJsDateToIsoDateTime(nullableLuxonToIsoDateTime(nullable(isoDateTime())));

/**
 * String type which allows for javascript date objects
 */
export const stringDate = () => coerce(string(), date(), date_ => date_.toISOString());